// GradebookDialog.js
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  InputAdornment,
  IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { db } from '../Configuration';
import * as XLSX from 'xlsx-js-style';
import { isAdmin, isPresident } from '../utils/permissions';
import {
  dedupeAttendanceBuckets,
  ATTENDANCE_PCT_WARN_MIN,
  isAttendanceGoodStanding
} from '../utils/attendanceMath';
import { attendanceCourseNameFromTestCourseId } from '../utils/courseAttendance';

/**
 * Gradebook-style marks entry dialog.
 * - Rows: students
 * - Columns: tests (Test 1, Test 2, ...)
 * - Cells: editable marks
 * - One Save button to update all students in bulk
 */
const GradebookDialog = ({ open, onClose, tests = [], students = [], marksByStudent = {}, courseLabel: propCourseLabel = '', courses = [] }) => {
  const [marks, setMarks] = useState({});
  const [inputValues, setInputValues] = useState({}); // Local input values for immediate UI update
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Build a stable list of test meta (id/name/maxScore) using test.id
  // Sort by courseId (subject) first, then by sequence within each subject
  const testColumns = useMemo(() => {
    // Create a sorted copy of tests
    const sortedTests = [...tests].sort((a, b) => {
      // First sort by courseId (subject)
      const courseA = a.courseId || '';
      const courseB = b.courseId || '';
      if (courseA !== courseB) {
        return courseA.localeCompare(courseB);
      }
      // Within same course, sort by sequence
      const seqA = a.sequence || a.testName || '';
      const seqB = b.sequence || b.testName || '';
      // Try to parse as numbers first for numeric sorting
      const numA = Number(seqA);
      const numB = Number(seqB);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      // Otherwise string compare
      return String(seqA).localeCompare(String(seqB), undefined, { numeric: true, sensitivity: 'base' });
    });

    return sortedTests.map((test, index) => {
      let label = test.testName || `Test ${index + 1}`;
      // Add "B.Q" prefix for test ID 108 (Bayan Ul Quran)
      const testId = test.id || `${test.courseId}-${test.testName}-${index}`;
      if (testId === '108' || testId.toString().includes('108') || testId.toString().includes('bayan')) {
        if (!label.startsWith('B.Q')) {
          label = `B.Q ${label}`;
        }
      }
      return {
        id: testId,
        label: label,
        maxScore: Number(test.maxScore ?? 0) || 0,
        courseId: test.courseId
      };
    });
  }, [tests]);

  // Filter students based on search query - direct filtering without debounce
  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) {
      return students;
    }
    const query = searchQuery.toLowerCase().trim();
    if (!query) return students;
    
    // Efficient filtering
    return students.filter(student => {
      const name = student.name?.toLowerCase() || '';
      const id = student.id?.toLowerCase() || '';
      return name.includes(query) || id.includes(query);
    });
  }, [students, searchQuery]);

  // Handle close - just close immediately
  const handleClose = () => {
    // Close immediately - no delays
    onClose();
  };

  // Cleanup after dialog fully exits
  const handleExited = () => {
    // Reset state after dialog has fully closed
    setMarks({});
    setInputValues({});
    setErrors({});
    setLoading(false);
    setSearchQuery('');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleMarkChange = (studentId, testId, maxScore, value) => {
    // Update input value immediately for responsive UI
    const inputKey = `${studentId}_${testId}`;
    setInputValues(prev => ({
      ...prev,
      [inputKey]: value
    }));

    // Update marks state immediately (no debounce)
    const num = value === '' ? '' : Number(value);

    setMarks(prev => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {}),
        [testId]: value
      }
    }));

    // Update errors immediately
    setErrors(prev => {
      const prevRow = prev[studentId] || {};
      let errorMsg = '';

      if (value !== '' && (isNaN(num) || num < 0)) {
        errorMsg = 'Enter a valid number (0 or more)';
      } else if (value !== '' && maxScore && num > maxScore) {
        errorMsg = `Cannot exceed max ${maxScore}`;
      }

      return {
        ...prev,
        [studentId]: {
          ...prevRow,
          [testId]: errorMsg
        }
      };
    });
  };

  const hasAnyError = useMemo(() => {
    return Object.values(errors).some(row =>
      row && Object.values(row).some(msg => !!msg)
    );
  }, [errors]);

  const saveMarks = async (closeAfterSave = false) => {
    if (hasAnyError) {
      showSnackbar('Please fix validation errors before saving.', 'error');
      return;
    }

    if (!students.length || !testColumns.length) {
      showSnackbar('No students or tests to save.', 'error');
      return;
    }

    if (filteredStudents.length === 0) {
      showSnackbar('No students visible to save. Please clear your search.', 'warning');
      return;
    }

    setLoading(true);
    try {
      // Save each student's edited marks into StudentMarks collection
      const savePromises = [];
      
      for (const student of students) {
        const rowMarks = marks[student.id] || {};

        for (const col of testColumns) {
          if (!Object.prototype.hasOwnProperty.call(rowMarks, col.id)) continue;
          const rawValue = rowMarks[col.id];
          const num = rawValue === '' || rawValue === undefined || rawValue === null
            ? 0
            : Number(rawValue) || 0;

          const markDocId = `${student.id}_${col.id}`;
          const markDocRef = doc(collection(db, 'StudentMarks'), markDocId);
          savePromises.push(
            setDoc(markDocRef, {
              studentId: student.id,
              testId: col.id,
              courseId: col.courseId || tests.find(t => t.id === col.id)?.courseId || '',
              campusCode: student.campusCode || '',
              mark: num
            }, { merge: true })
          );
        }
      }

      await Promise.all(savePromises);
      showSnackbar('Data saved', 'success');

      if (closeAfterSave) {
        // Wait a bit to show the notification before closing
        setTimeout(() => {
          onClose();
        }, 500);
      }
    } catch (error) {
      console.error('Error saving gradebook marks:', error);
      showSnackbar('Error saving marks. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await saveMarks(false); // Keep dialog open
  };

  const handleSaveAndExit = async () => {
    await saveMarks(true); // Close dialog after save
  };

  // Use prop courseLabel if provided, otherwise compute from tests
  const courseLabel = useMemo(() => {
    if (propCourseLabel) return propCourseLabel;
    if (!tests || !tests.length) return '';
    const first = tests[0];
    return first.courseName || first.courseId || '';
  }, [propCourseLabel, tests]);

  const handleCellKeyDown = (event, rowIndex, colIndex) => {
    if (event.key === 'Tab') {
      event.preventDefault();

      const direction = event.shiftKey ? -1 : 1; // Shift+Tab moves up, Tab moves down
      const nextRowIndex = rowIndex + direction;

      if (nextRowIndex >= 0 && nextRowIndex < filteredStudents.length) {
        const nextId = `grade-input-${nextRowIndex}-${colIndex}`;
        const el = document.getElementById(nextId);
        if (el) {
          el.focus();
          el.select();
        }
      }
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (!loading && !hasAnyError) {
        handleSave(); // Save but keep dialog open
      }
    }
  };

  const handleExportMarksheet = useCallback(async () => {
    if (!filteredStudents.length || !testColumns.length) {
      showSnackbar('No data to export.', 'warning');
      return;
    }

    setLoading(true);
    try {
      // Get course name for filename
      let courseName = courseLabel;
      if (!courseName && tests.length > 0) {
        courseName = tests[0].courseName || tests[0].courseId || '';
      }
      if (!courseName) {
        courseName = 'All Courses';
      }

      // Fetch attendance data for each course/subject
      const attendanceRef = collection(db, 'Attendance');

      // Group tests by courseId and get course names
      const testsByCourse = {};
      const courseNamesMap = {};
      testColumns.forEach(col => {
        const courseIdKey = col.courseId || 'default';
        if (!testsByCourse[courseIdKey]) {
          testsByCourse[courseIdKey] = [];
          courseNamesMap[courseIdKey] = attendanceCourseNameFromTestCourseId(
            courseIdKey,
            courses,
            tests
          );
        }
        testsByCourse[courseIdKey].push(col);
      });

      // Fetch attendance for each course/subject
      const attendanceByCourse = {};
      await Promise.all(Object.keys(testsByCourse).map(async (courseIdKey) => {
        const courseName = courseNamesMap[courseIdKey];
        const courseAttendanceSnapshot = await getDocs(query(attendanceRef, where('courseName', '==', courseName)));
        
        const courseAttendance = {};
        filteredStudents.forEach(student => {
          courseAttendance[student.id] = { present: 0, total: 0 };
        });

        courseAttendanceSnapshot.forEach(doc => {
          const data = doc.data();
          const sessionCampus = data.campusCode;
          const { presentSet, leaveSet } = dedupeAttendanceBuckets(data);

          filteredStudents.forEach(student => {
            if (!courseAttendance[student.id]) return;
            // Only count sessions for this student's campus (same course name can exist on multiple campuses)
            if (sessionCampus && student.campusCode && student.campusCode !== sessionCampus) {
              return;
            }
            courseAttendance[student.id].total += 1;
            if (presentSet.has(student.id) || leaveSet.has(student.id)) {
              courseAttendance[student.id].present += 1;
            }
          });
        });

        attendanceByCourse[courseIdKey] = courseAttendance;
      }));

      // Build headers: Student Name, Student ID, then for each course: Tests, Total (Subject), Percentage (Subject), Attendance (Subject)
      const headers = ['Student Name', 'Student ID'];
      const courseHeaders = [];
      
      Object.keys(testsByCourse).forEach(courseIdKey => {
        const courseName = courseNamesMap[courseIdKey];
        const courseTests = testsByCourse[courseIdKey];
        
        // Add test columns with B.Q prefix for test 108
        courseTests.forEach(col => {
          let label = col.label;
          // Add "B.Q" prefix for test ID 108 (Bayan Ul Quran)
          if (col.id === '108' || col.id.toString().includes('108') || col.id.toString().includes('bayan')) {
            if (!label.startsWith('B.Q')) {
              label = `B.Q ${label}`;
            }
          }
          courseHeaders.push(label);
        });
        
        // Add Total and Percentage columns with subject name in brackets (Out of value will be in row 1 above Total)
        courseHeaders.push(`Total (${courseName})`, `Percentage (${courseName})`);
        
        // Add Attendance columns for this subject with subject name in brackets
        courseHeaders.push(
          `Attendance (present+leave) (${courseName})`,
          `Attendance (sessions) (${courseName})`,
          `Attendance % (${courseName})`
        );
      });
      
      headers.push(...courseHeaders);
      
      // Add final summary columns (Out of value will be in row 1 above Grand Total Obtained)
      headers.push('Grand Total Obtained (marks)', 'Percentage (marks)');

      // First, determine which tests are conducted (if ANY student has marks > 0 for that test)
      const conductedTestsMap = {};
      filteredStudents.forEach(student => {
        testColumns.forEach(col => {
          const editedValue = marks[student.id]?.[col.id];
          const originalValue = marksByStudent[student.id]?.[col.id];
          const displayValue = editedValue !== undefined ? editedValue : (originalValue ?? '');
          const markValue = displayValue === '' ? 0 : Number(displayValue) || 0;
          
          // If ANY student has marks > 0 for this test, mark it as conducted
          if (markValue > 0 && !conductedTestsMap[col.id]) {
            conductedTestsMap[col.id] = true;
          }
        });
      });
      
      // Prepare data rows
      const rows = filteredStudents.map(student => {
        const row = [student.name || '', student.id || ''];
        
        // Track grand totals across all courses
        let grandTotalObtained = 0;
        let grandOutOf = 0;
        
        // Process each course group
        Object.keys(testsByCourse).forEach(courseIdKey => {
          const courseTests = testsByCourse[courseIdKey];
          let courseTotal = 0;
          let conductedTestsMaxTotal = 0;
          
          // Get marks for each test in this course
          courseTests.forEach(col => {
            const editedValue = marks[student.id]?.[col.id];
            const originalValue = marksByStudent[student.id]?.[col.id];
            const displayValue = editedValue !== undefined ? editedValue : (originalValue ?? '');
            const markValue = displayValue === '' ? 0 : Number(displayValue) || 0;
            row.push(markValue);
            courseTotal += markValue;
            
            // Only add to max total if this test is conducted (ANY student has marks > 0)
            if (conductedTestsMap[col.id]) {
              conductedTestsMaxTotal += (col.maxScore || 0);
            }
          });
          
          // Add total and percentage for this course (Out of value is in row 1 above Total column)
          row.push(courseTotal);
          // Only calculate percentage using conducted tests (where ANY student has marks > 0)
          const percentage = (conductedTestsMaxTotal > 0) 
            ? ((courseTotal / conductedTestsMaxTotal) * 100).toFixed(2) 
            : '';
          row.push(percentage === '' ? '' : parseFloat(percentage));
          
          // Accumulate grand totals
          grandTotalObtained += courseTotal;
          grandOutOf += conductedTestsMaxTotal;
          
          // Add attendance data for this subject
          const courseAttendance = attendanceByCourse[courseIdKey]?.[student.id] || { present: 0, total: 0 };
          const attendancePercentage = courseAttendance.total > 0 
            ? ((courseAttendance.present / courseAttendance.total) * 100).toFixed(2) 
            : '0.00';
          row.push(courseAttendance.present);
          row.push(courseAttendance.total);
          row.push(parseFloat(attendancePercentage));
        });
        
        // Add grand total columns at the end (Out of value is in row 1 above Grand Total Obtained)
        row.push(grandTotalObtained);
        const grandPercentage = (grandOutOf > 0) 
          ? ((grandTotalObtained / grandOutOf) * 100).toFixed(2) 
          : '';
        row.push(grandPercentage === '' ? '' : parseFloat(grandPercentage));
        
        return row;
      });

      // Create "Out of" row showing max marks for each test
      const outOfRow = ['Out of', '']; // Student Name, Student ID
      let grandOutOfTotal = 0;
      
      // For each course group, add max marks for tests
      Object.keys(testsByCourse).forEach(courseIdKey => {
        const courseTests = testsByCourse[courseIdKey];
        let courseOutOfTotal = 0;
        
        courseTests.forEach(col => {
          // Add max marks for this test
          outOfRow.push(col.maxScore || 0);
          // Calculate total "Out of" for conducted tests only
          if (conductedTestsMap[col.id]) {
            courseOutOfTotal += (col.maxScore || 0);
          }
        });
        
        // Add "Out of" value above Total column, then empty cells for Percentage and Attendance columns (5 columns per course)
        outOfRow.push(courseOutOfTotal, '', '', '', ''); // Out of value above Total, Percentage, Attendance Present, Attendance Total, Attendance %
        grandOutOfTotal += courseOutOfTotal;
      });
      
      // Add "Out of" value above Grand Total Obtained column, then empty cell for Percentage
      outOfRow.push(grandOutOfTotal, '');
      
      // Create worksheet with headers, out of row, and data rows
      const worksheetData = [headers, outOfRow, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);

      // Set column widths
      const colWidths = [
        { wch: 25 }, // Student Name
        { wch: 15 }, // Student ID
      ];
      
      // Add widths for each course group
      Object.keys(testsByCourse).forEach(courseIdKey => {
        const courseTests = testsByCourse[courseIdKey];
        courseTests.forEach(() => colWidths.push({ wch: 12 })); // Test columns
        colWidths.push({ wch: 18 }); // Total column (wider for subject name in brackets, Out of value in row 1)
        colWidths.push({ wch: 18 }); // Percentage column (wider for subject name in brackets)
        colWidths.push({ wch: 20 }, { wch: 20 }, { wch: 18 }); // Attendance columns (wider for subject name in brackets)
      });
      
      // Add widths for final summary columns (Out of value is in row 1 above Grand Total Obtained)
      colWidths.push({ wch: 20 }); // Grand Total Obtained
      colWidths.push({ wch: 18 }); // Percentage
      
      ws['!cols'] = colWidths;

      // Apply styling to cells
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      // Define styles
      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
        fill: { fgColor: { rgb: '4472C4' } }, // Blue background
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };

      const studentNameStyle = {
        font: { bold: true, sz: 10 },
        fill: { fgColor: { rgb: 'E7E6E6' } }, // Light gray background
        alignment: { vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const studentIdStyle = {
        font: { sz: 10 },
        fill: { fgColor: { rgb: 'F2F2F2' } }, // Very light gray background
        alignment: { vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const totalStyle = {
        font: { bold: true, sz: 10 },
        fill: { fgColor: { rgb: 'FFF2CC' } }, // Light yellow background
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const percentageStyle = {
        font: { bold: true, sz: 10, color: { rgb: '006100' } }, // Green text
        fill: { fgColor: { rgb: 'C6EFCE' } }, // Light green background
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const grandTotalStyle = {
        font: { bold: true, sz: 10 },
        fill: { fgColor: { rgb: 'FFF2CC' } }, // Light yellow background (same as total columns)
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const grandPercentageStyle = {
        font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, // White text
        fill: { fgColor: { rgb: '70AD47' } }, // Green background
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'medium', color: { rgb: '000000' } },
          bottom: { style: 'medium', color: { rgb: '000000' } },
          left: { style: 'medium', color: { rgb: '000000' } },
          right: { style: 'medium', color: { rgb: '000000' } }
        }
      };

      const attendanceStyle = {
        font: { sz: 10 },
        fill: { fgColor: { rgb: 'D9E1F2' } }, // Light blue background
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const defaultStyle = {
        font: { sz: 10 },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      // Style header row (row 0)
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
        ws[cellAddress].s = headerStyle;
      }
      
      // Style "Out of" row (row 1)
      const outOfRowStyle = {
        font: { bold: true, sz: 10 },
        fill: { fgColor: { rgb: 'D9E1F2' } }, // Light blue background
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };
      
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 1, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].s = outOfRowStyle;
        }
      }

      // Track column positions for styling
      let currentCol = 0;
      const columnTypes = []; // Track what type each column is
      
      // Student Name and ID columns
      columnTypes.push('name');
      columnTypes.push('id');
      currentCol = 2;

      // Process each course group to identify column types
      Object.keys(testsByCourse).forEach(courseIdKey => {
        const courseTests = testsByCourse[courseIdKey];
        // Test columns
        courseTests.forEach(() => {
          columnTypes.push('test');
        });
        // Total, Percentage columns (Out of value is in row 1 above Total)
        columnTypes.push('total');
        columnTypes.push('percentage');
        // Attendance columns
        columnTypes.push('attendance');
        columnTypes.push('attendance');
        columnTypes.push('attendance');
      });
      
      // Add final summary column types (Out of value is in row 1 above Grand Total Obtained)
      columnTypes.push('grandtotal');
      columnTypes.push('grandpercentage');

      // Style data rows (starting from row 2, since row 0 is headers and row 1 is "Out of")
      for (let row = 2; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;
          
          const colType = columnTypes[col];
          let cellStyle = defaultStyle;

          if (colType === 'name') {
            cellStyle = studentNameStyle;
          } else if (colType === 'id') {
            cellStyle = studentIdStyle;
          } else if (colType === 'total') {
            cellStyle = totalStyle;
          } else if (colType === 'percentage') {
            // Marks percentage: conditional color based on value
            const percentageValue = ws[cellAddress].v;
            let backgroundColor = 'C6EFCE'; // Light green (default)
            let textColor = '006100'; // Dark green text (default)
            
            if (percentageValue !== '' && percentageValue !== null && !isNaN(percentageValue)) {
              const percent = parseFloat(percentageValue);
              // Marks: >= 40% = light green with dark green text, < 40% = red with white text
              if (percent >= 40) {
                backgroundColor = 'C6EFCE'; // Light green
                textColor = '006100'; // Dark green text
              } else {
                backgroundColor = 'F44336'; // Red
                textColor = 'FFFFFF'; // White text
              }
            }
            
            cellStyle = {
              font: { bold: true, sz: 10, color: { rgb: textColor } },
              fill: { fgColor: { rgb: backgroundColor } },
              alignment: { horizontal: 'center', vertical: 'center' },
              border: {
                top: { style: 'thin', color: { rgb: 'CCCCCC' } },
                bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
                left: { style: 'thin', color: { rgb: 'CCCCCC' } },
                right: { style: 'thin', color: { rgb: 'CCCCCC' } }
              }
            };
          } else if (colType === 'attendance') {
            cellStyle = attendanceStyle;
          } else if (colType === 'grandtotal') {
            cellStyle = grandTotalStyle;
          } else if (colType === 'grandpercentage') {
            // Grand percentage: conditional color based on value
            const percentageValue = ws[cellAddress].v;
            let backgroundColor = 'C6EFCE'; // Light green (default)
            let textColor = '006100'; // Dark green text (default)
            
            if (percentageValue !== '' && percentageValue !== null && !isNaN(percentageValue)) {
              const percent = parseFloat(percentageValue);
              // Marks: >= 40% = light green with dark green text, < 40% = red with white text
              if (percent >= 40) {
                backgroundColor = 'C6EFCE'; // Light green
                textColor = '006100'; // Dark green text
              } else {
                backgroundColor = 'F44336'; // Red
                textColor = 'FFFFFF'; // White text
              }
            }
            
            cellStyle = {
              font: { bold: true, sz: 11, color: { rgb: textColor } },
              fill: { fgColor: { rgb: backgroundColor } },
              alignment: { horizontal: 'center', vertical: 'center' },
              border: {
                top: { style: 'medium', color: { rgb: '000000' } },
                bottom: { style: 'medium', color: { rgb: '000000' } },
                left: { style: 'medium', color: { rgb: '000000' } },
                right: { style: 'medium', color: { rgb: '000000' } }
              }
            };
          }

          ws[cellAddress].s = cellStyle;
        }
      }

      // Create workbook and add worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Marksheet');

      // Generate filename with course and date
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `Gradebook_${courseName.replace(/[^a-zA-Z0-9]/g, '_')}_${dateStr}.xlsx`;

      // Download file with styling
      XLSX.writeFile(wb, filename);
      showSnackbar(`Marksheet exported successfully: ${filename}`, 'success');
    } catch (error) {
      console.error('Error exporting marksheet:', error);
      showSnackbar('Error exporting marksheet. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filteredStudents, testColumns, marks, marksByStudent, courseLabel, tests, courses, showSnackbar]);

  const handleExportResultExcel = useCallback(async () => {
    if (!filteredStudents.length || !testColumns.length) {
      showSnackbar('No data to export.', 'warning');
      return;
    }

    setLoading(true);
    try {
      // Required subjects with weightages (total: 1000)
      // Read course names directly from courses table, only Bayan ul Quran is hardcoded
      const requiredSubjectsConfig = [
        { name: 'Arabic Grammer', weightage: 200 },
        { name: 'Muntakhab Nisaab', weightage: 200 },
        { name: 'Bayan ul Quran', weightage: 200, isHardcoded: true }, // Hardcoded
        { name: 'Hadees-wa-sunnat', weightage: 100 },
        { name: 'Tajweed-ul-Quran', weightage: 100 },
        { name: 'Tarjuma-e-Quran', weightage: 100 },
        { name: 'Fikr e Islami', weightage: 100 }
      ];

      // Group tests by courseId (same as working export)
      const testsByCourse = {};
      testColumns.forEach(col => {
        const courseIdKey = col.courseId || 'default';
        if (!testsByCourse[courseIdKey]) {
          testsByCourse[courseIdKey] = [];
        }
        testsByCourse[courseIdKey].push(col);
      });

      // Build subject mapping - match courses to required subjects
      const subjectMapping = [];
      
      requiredSubjectsConfig.forEach((config, index) => {
        if (config.isHardcoded) {
          // Bayan ul Quran - hardcoded (find tests by ID 108 or bayan- prefix)
          const bayanTests = testColumns.filter(col => {
            const isBayanTest = col.id === '108' || 
                               col.id === 108 || 
                               String(col.id).includes('108') ||
                               String(col.id).startsWith('bayan-');
            return isBayanTest;
          });
          
          subjectMapping.push({
            courseId: 'bayan-hardcoded',
            courseName: 'Bayan ul Quran',
            weightage: config.weightage,
            tests: bayanTests
          });
        } else {
          // Find course from courses table - try multiple matching strategies
          let matchedCourse = null;
          let matchedTests = [];
          
          // Strategy 1: Match by courseId first (most reliable - we know tests have courseId)
          // Check all courseIds that have tests and see if their course name matches
          Object.keys(testsByCourse).forEach(courseIdKey => {
            if (matchedCourse) return; // Already matched
            
            const testCourse = courses.find(c => 
              c.id === courseIdKey || c.id === String(courseIdKey)
            );
            
            if (testCourse && testCourse.name) {
              const configNameLower = config.name.toLowerCase().trim();
              const courseNameLower = testCourse.name.toLowerCase().trim();
              
              // Exact match
              if (courseNameLower === configNameLower) {
                matchedCourse = testCourse;
                matchedTests = testsByCourse[courseIdKey] || [];
                return;
              }
              
              // Flexible matching (handles misspellings like "Grammer" vs "Grammar")
              if (courseNameLower.includes(configNameLower) || 
                  configNameLower.includes(courseNameLower) ||
                  // Handle common misspellings
                  (configNameLower.includes('arabic grammar') && courseNameLower.includes('arabic grammer')) ||
                  (configNameLower.includes('arabic grammer') && courseNameLower.includes('arabic grammar'))) {
                matchedCourse = testCourse;
                matchedTests = testsByCourse[courseIdKey] || [];
              }
            }
          });
          
          // Strategy 2: If still not matched, try exact name match (in case courseId doesn't match)
          if (!matchedCourse) {
            matchedCourse = courses.find(c => 
              c.name && c.name.toLowerCase().trim() === config.name.toLowerCase().trim()
            );
            
            if (matchedCourse) {
              matchedTests = testsByCourse[matchedCourse.id] || [];
            }
          }
          
          if (matchedCourse) {
            // Get all tests for this courseId
            const courseTests = matchedTests.length > 0 ? matchedTests : (testsByCourse[matchedCourse.id] || []);
            subjectMapping.push({
              courseId: matchedCourse.id,
              courseName: matchedCourse.name,
              weightage: config.weightage,
              tests: courseTests
            });
          } else {
            // Course not found - still include with placeholder
            subjectMapping.push({
              courseId: `subject-${index}`,
              courseName: config.name,
              weightage: config.weightage,
              tests: []
            });
          }
        }
      });

      // Build headers: Student Name, Student ID, then each subject: Total, Normalized Marks, Percentage
      const headers = ['Student Name', 'Student ID'];
      const subjectHeaders = [];
      
      subjectMapping.forEach(subject => {
        subjectHeaders.push(
          `Total (${subject.courseName})`, 
          `Normalized Marks (${subject.courseName})`,
          `Percentage (${subject.courseName})`
        );
      });
      
      headers.push(...subjectHeaders);
      headers.push('Grand Total Obtained', 'Weightage', 'Percentage', 'Total Attendance', 'Total Attendance Percentage');

      // Create "Weightage" row showing weightages above Normalized Marks column
      const outOfRow = ['Weightage', ''];
      subjectMapping.forEach(subject => {
        outOfRow.push('', subject.weightage, ''); // Total (empty), Normalized Marks (weightage), Percentage (empty)
      });
      outOfRow.push('', '', '', '', ''); // Grand Total columns and Attendance columns

      // Fetch attendance for all subjects
      const attendanceRef = collection(db, 'Attendance');
      const attendanceBySubject = {};
      
      await Promise.all(subjectMapping.map(async (subject) => {
        if (subject.isHardcoded && subject.courseName === 'Bayan ul Quran') {
          // Skip Bayan ul Quran for attendance (or handle separately if needed)
          attendanceBySubject[subject.courseName] = {};
          filteredStudents.forEach(student => {
            attendanceBySubject[subject.courseName][student.id] = { present: 0, total: 0 };
          });
          return;
        }
        
        const courseName = subject.courseName;
        const courseAttendanceSnapshot = await getDocs(query(attendanceRef, where('courseName', '==', courseName)));
        
        const courseAttendance = {};
        filteredStudents.forEach(student => {
          courseAttendance[student.id] = { present: 0, total: 0 };
        });

        courseAttendanceSnapshot.forEach(doc => {
          const data = doc.data();
          const sessionCampus = data.campusCode;
          const { presentSet, leaveSet } = dedupeAttendanceBuckets(data);

          filteredStudents.forEach(student => {
            if (!courseAttendance[student.id]) return;
            if (sessionCampus && student.campusCode && student.campusCode !== sessionCampus) {
              return;
            }
            courseAttendance[student.id].total += 1;
            if (presentSet.has(student.id) || leaveSet.has(student.id)) {
              courseAttendance[student.id].present += 1;
            }
          });
        });

        attendanceBySubject[courseName] = courseAttendance;
      }));

      // Prepare data rows
      const rows = filteredStudents.map(student => {
        const row = [student.name || '', student.id || ''];
        let grandTotalObtained = 0;
        let grandOutOf = 0;
        
        // Process each subject (in order)
        subjectMapping.forEach(subjectData => {
          const weightage = subjectData.weightage;
          let subjectTotalObtained = 0;
          let subjectTotalOutOf = 0;
          
          // Sum all marks and max scores for this subject (if tests exist)
          if (subjectData && subjectData.tests && subjectData.tests.length > 0) {
            subjectData.tests.forEach(col => {
              // Access marks using the test ID from col.id
              const testId = col.id;
              const editedValue = marks[student.id]?.[testId];
              const originalValue = marksByStudent[student.id]?.[testId];
              const displayValue = editedValue !== undefined ? editedValue : (originalValue ?? '');
              const markValue = displayValue === '' ? 0 : Number(displayValue) || 0;
              subjectTotalObtained += markValue;
              subjectTotalOutOf += col.maxScore || 0;
            });
          }
          
          // Calculate percentage: (obtained / total out of) * 100
          // If no tests (0/0), show 0% and 0 normalized marks
          let normalizedMarks = 0;
          let percentage = '0.00';
          
          if (subjectTotalOutOf > 0) {
            const actualPercentage = (subjectTotalObtained / subjectTotalOutOf) * 100;
            normalizedMarks = (actualPercentage / 100) * weightage;
            percentage = actualPercentage.toFixed(2);
          } else {
            // No tests conducted: 0 obtained, 0 out of, 0 normalized
            normalizedMarks = 0;
            percentage = '0.00';
          }
          
          row.push(subjectTotalObtained);
          row.push(parseFloat(normalizedMarks.toFixed(2))); // Normalized Marks
          row.push(parseFloat(percentage)); // Percentage
          
          // Accumulate grand totals (use normalized marks for grand total)
          grandTotalObtained += normalizedMarks;
          grandOutOf += weightage; // Always add weightage (total should be 1000)
        });
        
        // Add grand total columns (Weightage is always 1000)
        row.push(parseFloat(grandTotalObtained.toFixed(2)));
        row.push(1000); // Always 1000 (sum of all weightages: 200+200+200+100+100+100+100)
        const grandPercentage = 1000 > 0 
          ? ((grandTotalObtained / 1000) * 100).toFixed(2) 
          : '0.00';
        row.push(parseFloat(grandPercentage));
        
        // Calculate total attendance across all subjects
        let totalPresent = 0;
        let totalClasses = 0;
        
        subjectMapping.forEach(subject => {
          const subjectAttendance = attendanceBySubject[subject.courseName]?.[student.id] || { present: 0, total: 0 };
          totalPresent += subjectAttendance.present;
          totalClasses += subjectAttendance.total;
        });
        
        row.push(totalPresent); // Total Attendance
        const totalAttendancePercentage = totalClasses > 0 
          ? ((totalPresent / totalClasses) * 100).toFixed(2) 
          : '0.00';
        row.push(parseFloat(totalAttendancePercentage)); // Total Attendance Percentage
        
        return row;
      });

      // Create worksheet
      const worksheetData = [headers, outOfRow, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);

      // Set column widths
      const colWidths = [
        { wch: 25 }, // Student Name
        { wch: 15 }, // Student ID
      ];
      
      subjectMapping.forEach(() => {
        colWidths.push({ wch: 18 }); // Total
        colWidths.push({ wch: 22 }); // Normalized Marks
        colWidths.push({ wch: 18 }); // Percentage
      });
      
      colWidths.push({ wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 25 }); // Grand Total columns and Attendance columns
      ws['!cols'] = colWidths;

      // Apply styling
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
        fill: { fgColor: { rgb: '4472C4' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };

      const outOfRowStyle = {
        font: { bold: true, sz: 10 },
        fill: { fgColor: { rgb: 'D9E1F2' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const studentNameStyle = {
        font: { bold: true, sz: 10 },
        fill: { fgColor: { rgb: 'E7E6E6' } },
        alignment: { vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const studentIdStyle = {
        font: { sz: 10 },
        fill: { fgColor: { rgb: 'F2F2F2' } },
        alignment: { vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const totalStyle = {
        font: { bold: true, sz: 10 },
        fill: { fgColor: { rgb: 'FFF2CC' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const percentageStyle = {
        font: { bold: true, sz: 10, color: { rgb: '006100' } },
        fill: { fgColor: { rgb: 'C6EFCE' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const grandTotalStyle = {
        font: { bold: true, sz: 10 },
        fill: { fgColor: { rgb: 'FFF2CC' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      const defaultStyle = {
        font: { sz: 10 },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      // Style header row
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
        ws[cellAddress].s = headerStyle;
      }
      
      // Style "Weightage" row
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 1, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].s = outOfRowStyle;
        }
      }

      // Track column types
      const columnTypes = [];
      columnTypes.push('name');
      columnTypes.push('id');
      
      subjectMapping.forEach(() => {
        columnTypes.push('total');
        columnTypes.push('normalized');
        columnTypes.push('percentage');
      });
      
      columnTypes.push('grandtotal');
      columnTypes.push('grandoutof');
      columnTypes.push('grandpercentage');
      columnTypes.push('attendance');
      columnTypes.push('attendancepercentage');

      // Style data rows
      for (let row = 2; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;
          
          const colType = columnTypes[col];
          let cellStyle = defaultStyle;

          if (colType === 'name') {
            cellStyle = studentNameStyle;
          } else if (colType === 'id') {
            cellStyle = studentIdStyle;
          } else if (colType === 'total' || colType === 'normalized' || colType === 'grandtotal' || colType === 'grandoutof' || colType === 'attendance') {
            cellStyle = totalStyle;
          } else if (colType === 'percentage' || colType === 'grandpercentage') {
            // Marks percentage: background color based on value
            const percentageValue = ws[cellAddress].v;
            let backgroundColor = 'C6EFCE'; // Light green (original shade)
            let textColor = '006100'; // Dark green text (original)
            
            if (percentageValue !== '' && percentageValue !== null && !isNaN(percentageValue)) {
              const percent = parseFloat(percentageValue);
              // Marks: >= 40% = light green with dark green text, < 40% = red with white text
              if (percent >= 40) {
                backgroundColor = 'C6EFCE'; // Light green
                textColor = '006100'; // Dark green text
              } else {
                backgroundColor = 'F44336'; // Red
                textColor = 'FFFFFF'; // White text
              }
            }
            
            cellStyle = {
              font: { bold: true, sz: 10, color: { rgb: textColor } },
              fill: { fgColor: { rgb: backgroundColor } },
              alignment: { horizontal: 'center', vertical: 'center' },
              border: {
                top: { style: 'thin', color: { rgb: 'CCCCCC' } },
                bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
                left: { style: 'thin', color: { rgb: 'CCCCCC' } },
                right: { style: 'thin', color: { rgb: 'CCCCCC' } }
              }
            };
          } else if (colType === 'attendancepercentage') {
            // Attendance percentage: background color based on value
            const percentageValue = ws[cellAddress].v;
            let backgroundColor = 'C6EFCE'; // Light green (original shade)
            let textColor = '006100'; // Dark green text (original)
            
            if (percentageValue !== '' && percentageValue !== null && !isNaN(percentageValue)) {
              const percent = parseFloat(percentageValue);
              // Attendance: > 70% green, 50–70% orange, < 50% red
              if (isAttendanceGoodStanding(percent)) {
                backgroundColor = 'C6EFCE'; // Light green
                textColor = '006100'; // Dark green text
              } else if (percent >= ATTENDANCE_PCT_WARN_MIN) {
                backgroundColor = 'FF9800'; // Orange
                textColor = 'FFFFFF'; // White text
              } else {
                backgroundColor = 'F44336'; // Red
                textColor = 'FFFFFF'; // White text
              }
            }
            
            cellStyle = {
              font: { bold: true, sz: 10, color: { rgb: textColor } },
              fill: { fgColor: { rgb: backgroundColor } },
              alignment: { horizontal: 'center', vertical: 'center' },
              border: {
                top: { style: 'thin', color: { rgb: 'CCCCCC' } },
                bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
                left: { style: 'thin', color: { rgb: 'CCCCCC' } },
                right: { style: 'thin', color: { rgb: 'CCCCCC' } }
              }
            };
          }

          ws[cellAddress].s = cellStyle;
        }
      }

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Result');

      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `Result_${dateStr}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);
      showSnackbar(`Result Excel exported successfully: ${filename}`, 'success');
    } catch (error) {
      console.error('Error exporting result excel:', error);
      showSnackbar('Error exporting result excel. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filteredStudents, testColumns, marks, marksByStudent, tests, courses, showSnackbar]);

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : handleClose}
        onExited={handleExited}
        maxWidth="xl"
        fullWidth
        fullScreen={false}
        disableEscapeKeyDown={loading}
        disableScrollLock
        TransitionProps={{
          timeout: 0,
          appear: false
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#4CAF50',
            color: 'white',
            py: 2
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Gradebook
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
              Update marks for all students and tests in one place. Edit values and click Save at the end.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={`${filteredStudents.length} student${filteredStudents.length === 1 ? '' : 's'}`}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600
              }}
            />
            <Chip
              label={`${testColumns.length} test${testColumns.length === 1 ? '' : 's'}`}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600
              }}
            />
            {(true || isPresident()) && (
              <>
                <Button
                  onClick={handleExportMarksheet}
                  color="inherit"
                  variant="outlined"
                  disabled={loading || !tests.length || !students.length || filteredStudents.length === 0}
                  startIcon={<FileDownloadIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)'
                    },
                    '&:disabled': {
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'rgba(255,255,255,0.5)'
                    }
                  }}
                >
                  Export Excel
                </Button>
                <Button
                  onClick={handleExportResultExcel}
                  color="inherit"
                  variant="outlined"
                  disabled={loading || !tests.length || !students.length || filteredStudents.length === 0}
                  startIcon={<FileDownloadIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)'
                    },
                    '&:disabled': {
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'rgba(255,255,255,0.5)'
                    }
                  }}
                >
                  Result Excel
                </Button>
              </>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {(!tests || !tests.length) || (!students || !students.length) ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography color="text.secondary">
                {!tests || !tests.length
                  ? 'No tests available for the selected course.'
                  : 'No students found for the selected campus.'}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: '#e8f5e9',
                    borderRadius: 2,
                    border: '1px solid #c8e6c9'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    📘 How to use Gradebook
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    - <strong>Left</strong>: Student names in the selected campus and filters.
                    <br />
                    - <strong>Top</strong>: Tests (Test 1, Test 2, ... with max marks).
                    <br />
                    - <strong>Cells</strong>: Type marks directly. Leave empty for 0. Values cannot exceed the max marks.
                    <br />
                    - When finished, click <strong>Save All Marks</strong> to update all students in one go.
                  </Typography>
                </Paper>
              </Box>

              <Box
                sx={{
                  mb: 1.5,
                  position: 'relative',
                  maxWidth: { xs: '100%', sm: 420 }
                }}
              >
                <SearchIcon
                  sx={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'text.secondary',
                    fontSize: 20,
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                />
                <input
                  type="text"
                  placeholder="Search by name or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 36px 8px 40px',
                    fontSize: '0.875rem',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    outline: 'none',
                    backgroundColor: 'white',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1976d2';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#ccc';
                  }}
                />
                {searchQuery && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      p: 0.5
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <TableContainer
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  border: '1px solid rgba(76, 175, 80, 0.25)',
                  maxHeight: '60vh'
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          bgcolor: '#f1f8e9',
                          p: 1.5,
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          minWidth: 220
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Student Name
                        </Typography>
                      </TableCell>
                      {testColumns.map(col => (
                        <TableCell
                          key={col.id}
                          align="center"
                          sx={{
                            fontWeight: 700,
                            bgcolor: '#f1f8e9',
                            minWidth: 120
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {col.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Max: {col.maxScore || '-'}
                            </Typography>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStudents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={testColumns.length + 1} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No students found matching your search.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStudents.map((student, rowIndex) => {
                        const rowErrors = errors[student.id] || {};
                        return (
                          <TableRow key={student.id} hover>
                            <TableCell
                              sx={{
                                position: 'sticky',
                                left: 0,
                                bgcolor: 'white',
                                zIndex: 1,
                                maxWidth: 240
                              }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {student.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {student.id}
                                </Typography>
                              </Box>
                            </TableCell>
                            {testColumns.map((col, colIndex) => {
                              const inputKey = `${student.id}_${col.id}`;
                              const inputValue = inputValues[inputKey];
                              const editedValue = marks[student.id]?.[col.id];
                              const originalValue = marksByStudent[student.id]?.[col.id];
                              
                              // Use inputValue if exists (for immediate UI feedback), otherwise use edited or original
                              const displayValue = inputValue !== undefined
                                ? inputValue
                                : (editedValue !== undefined
                                    ? editedValue
                                    : (originalValue ?? ''));
                              
                              return (
                                <TableCell key={col.id} align="center">
                                  <input
                                    id={`grade-input-${rowIndex}-${colIndex}`}
                                    type="number"
                                    value={displayValue}
                                    onChange={e =>
                                      handleMarkChange(
                                        student.id,
                                        col.id,
                                        col.maxScore,
                                        e.target.value
                                      )
                                    }
                                    onKeyDown={e => handleCellKeyDown(e, rowIndex, colIndex)}
                                    style={{
                                      width: '72px',
                                      padding: '4px 6px',
                                      fontSize: '0.8rem',
                                      textAlign: 'center',
                                      borderRadius: 4,
                                      border: rowErrors[col.id] ? '1px solid #f44336' : '1px solid #ccc',
                                      outline: 'none'
                                    }}
                                    min={0}
                                    max={col.maxScore}
                                  />
                                  {rowErrors[col.id] && (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      sx={{ display: 'block', mt: 0.5 }}
                                    >
                                      {rowErrors[col.id]}
                                    </Typography>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <Button onClick={handleClose} color="secondary" disabled={loading}>
            Close
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleSave}
            color="primary"
            variant="outlined"
            disabled={loading || hasAnyError || !tests.length || !students.length || filteredStudents.length === 0}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600, mr: 1 }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <Button
            onClick={handleSaveAndExit}
            color="primary"
            variant="contained"
            disabled={loading || hasAnyError || !tests.length || !students.length || filteredStudents.length === 0}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {loading ? 'Saving...' : 'Save and Exit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GradebookDialog;


